<script lang="ts">
	import classNames from 'classnames';
	import { DropCardVariant } from '../../constants/dropCardVariants';
	import type { DropSections } from '../../constants/dropSectionImports/types';
	import type {
		DropPageItem,
		ParsedDropSpinsStylesType,
		DropCountdownType,
		DropCountdownNoButtonType,
		DropHeroType,
		DropAnchorType,
	} from '../../constants/types';
	import { getDropButtonLink } from '../../utils/getDropButtonLink/getDropButtonLink';
	import { getDropButtonShowDownloadIcon } from '../../utils/getDropButtonShowDownloadIcon/getDropButtonShowDownloadIcon';
	import { getDropButtonText } from '../../utils/getDropButtonText/getDropButtonText';
	import { getDropButtonVariant } from '../../utils/getDropButtonVariant/getDropButtonVariant';
	import { getDropCardVariants } from '../../utils/getDropCardVariants/getDropCardVariants';
	import { getDropProductCardGridColumns } from '../../utils/getDropProductCardGridColumns';
	import { isDropAnchor } from '../../utils/isDropAnchor';
	import { isDropButton } from '../../utils/isDropButton';
	import { isDropCarousel } from '../../utils/isDropCarousel';
	import { isDropComponentBackground } from '../../utils/isDropComponentBackground';
	import { isDropCountdown } from '../../utils/isDropCountdown';
	import { isDropCountdownNoButton } from '../../utils/isDropCountdownNoButton/isDropCountdownNoButton';
	import { isDropFaqSection } from '../../utils/isDropFaqSection';
	import { isDropGallery } from '../../utils/isDropGallery';
	import { isDropGfgArtwork } from '../../utils/isDropGfgArtwork/isDropGfgArtwork';
	import { isDropGFGPatches } from '../../utils/isDropGFGPatches';
	import { isDropHero } from '../../utils/isDropHero';
	import { isDropHybridFormats } from '../../utils/isDropHybridFormats/isDropHybridFormats';
	import { isDropHybridProductCardGrid } from '../../utils/isDropHybridProductCardGrid/isDropHybridProductCardGrid';
	import { isDropImage } from '../../utils/isDropImage';
	import { isDropImageCarousel } from '../../utils/isDropImageCarousel';
	import { isDropLogos } from '../../utils/isDropLogos';
	import { isDropMadcCarousel } from '../../utils/isDropMadcCarousel';
	import { isDropNewsletter } from '../../utils/isDropNewsletter';
	import { isDropNftVideo } from '../../utils/isDropNftVideo/isDropNftVideo';
	import { isDropPlatform } from '../../utils/isDropPlatform';
	import { isDropProductCardGrid } from '../../utils/isDropProductCardGrid';
	import { isDropProductCards } from '../../utils/isDropProductCards';
	import { isDropQuote } from '../../utils/isDropQuote';
	import { isDropSlideshow } from '../../utils/isDropSlideshow';
	import { isDropSpinsAlgorithmInformation } from '../../utils/isDropSpinsAlgorithmInformation';
	import { isDropSpinsFormat } from '../../utils/isDropSpinsFormat';
	import { isDropSpinsStyles } from '../../utils/isDropSpinsStyles';
	import { isDropTabBar } from '../../utils/isDropTabBar/isDropTabBar';
	import { isDropTabBarEnd } from '../../utils/isDropTabBarEnd/isDropTabBarEnd';
	import { isDropTextSection } from '../../utils/isDropTextSection';
	import { isDropTheCurrencyAntiCounterfeinting } from '../../utils/isDropTheCurrencyAntiCounterfeinting/isDropTheCurrencyAntiCounterfeinting';
	import { isDropTimeline } from '../../utils/isDropTimeline';
	import { isDropVideo } from '../../utils/isDropVideo';
	import { isDropWhatsappButton } from '../../utils/isDropWhatsappButton';
	import { shouldHideDropButton } from '../../utils/shouldHideDropButton/shouldHideDropButton';
	import { DropVariants } from '../DropVariants';
	import { EditionsRoutes } from '#lib/websites/editions/constants/routes.js';
	import { page } from '$app/state';
	import { Container } from '$global/components/Container';
	import { LinkButton } from '$global/components/LinkButton';
	import { Skeleton } from '$global/components/Skeleton';
	import type { ContentKey } from '$global/utils/contentKey';

	interface Props {
		pastDropType: string | null | undefined;
		// The items with their Markdown parsed on the server
		// (`parseDropComponentsMarkdownServer`).
		componentItems: DropPageItem[];
		// The components those items render, imported by the route's universal
		// load (`loadDropSections`) before this renders, so a drop page ships
		// only the section types it uses.
		sections: DropSections;
		showRegisterCode: string | null | undefined;
		showCardButtons: boolean;
		showCardCartButton: boolean;
		productId: string;
		dropConfigId: string | null | undefined;
		// Supplied by the drop's loader (HB-974): this component has no artwork
		// data of its own, so the register-interest content key is passed down.
		registerInterestContentKey?: ContentKey;
	}

	let {
		productId,
		dropConfigId,
		pastDropType,
		componentItems,
		sections,
		showRegisterCode,
		showCardButtons,
		showCardCartButton,
		registerInterestContentKey,
	}: Props = $props();

	let {
		DropAnchor,
		DropButton,
		DropCarousel,
		DropComponentBackground,
		DropCountdown,
		DropCountdownButtons,
		DropFaqSection,
		DropGallery,
		DropGfgArtwork,
		DropGfgPatches,
		DropHero,
		DropHybridFormats,
		DropHybridProductCardGrid,
		DropImage,
		DropImageCarousel,
		DropLogos,
		DropMadcCarousel,
		DropNewsletter,
		DropNftVideo,
		DropPlatform,
		DropProductCardGrid,
		DropProductCards,
		DropQuote,
		DropSlideshow,
		DropSpinsAlgorithmInformation,
		DropSpinsFormat,
		DropSpinsStyles,
		DropTabBar,
		DropTopBarEnd,
		DropTextSection,
		DropTheCurrencyAntiCounterfeinting,
		DropTimeline,
		DropVideo,
		DropWhatsappButton,
		RegisterInterestButtons,
	} = $derived(sections);

	// The mobile layouts each grid offers in Directus' `variants` checkboxes.
	// Drop_Product_Cards has none: it showcases one to a few artworks at full
	// size, which a compact layout would defeat.
	const PRODUCT_GRID_VARIANTS = Object.values(DropCardVariant);
	const HYBRID_GRID_VARIANTS = Object.values(DropCardVariant);

	let anchorRefs: HTMLSpanElement[] = $state([]);

	let anchors = $derived(
		(() => {
			// Needs to react to every drop page update
			if (
				showCardButtons ||
				!showCardButtons ||
				showCardCartButton ||
				!showCardCartButton
				// eslint-disable-next-line no-empty
			) {
			}

			return componentItems.reduce(
				(
					accumulator: (DropAnchorType & {
						ref: HTMLSpanElement | undefined;
					})[],
					componentItem
				) => {
					if (
						isDropAnchor(componentItem) &&
						componentItem?.drop_tab_bar_label
					) {
						return [
							...accumulator,
							{
								...componentItem,
								ref: anchorRefs[accumulator.length],
							},
						];
					}

					return accumulator;
				},
				[]
			);
		})()
	);

	let heroItem = $derived(
		componentItems.find((componentItem) =>
			isDropHero(componentItem)
		) as DropHeroType
	);

	let countdownItem = $derived(
		componentItems.find(
			(componentItem) =>
				isDropCountdown(componentItem) || isDropCountdownNoButton(componentItem)
		) as DropCountdownType | DropCountdownNoButtonType
	);
</script>

<div class="pb-[9.5rem]">
	{#each componentItems as item, index (item)}
		{#if isDropTextSection(item)}
			<Container
				dataCy="drop-text-section"
				class={classNames(
					'mb-6 max-w-[720px] md:px-0',
					isDropQuote(componentItems?.[index - 1]) ? '' : 'mt-10 md:mt-16'
				)}
			>
				<DropTextSection {item} {showCardCartButton} />
			</Container>
		{:else if isDropHero(item)}
			<DropHero
				dataCy="drop"
				pastDropPage={!!pastDropType}
				{showCardButtons}
				{showCardCartButton}
				{item}
			/>

			<Container
				dataCy="drop-countdown"
				class={classNames('my-6 max-w-full sm:px-0 lg:hidden')}
			>
				{#if countdownItem}
					<DropCountdown
						pastDropPage={!!pastDropType}
						dataCy="drop"
						borderless
						item={countdownItem}
						contentKey={registerInterestContentKey}
					>
						{#if !showCardButtons && !pastDropType}
							<Skeleton
								variant="rectangular"
								class="mx-auto mt-[1rem] h-[2.5rem] w-[20.3125rem] max-w-full lg:hidden"
							/>
							<Skeleton
								variant="rectangular"
								class="mx-auto mt-[1rem] hidden h-[3rem] w-[10rem] lg:block"
							/>
						{:else if showCardCartButton && !pastDropType}
							<!-- The press kit button is desktop-only while the drop is live;
							the inactive branch below still shows its own button on mobile. -->
						{:else}
							<LinkButton
								variant="secondary"
								href={page.params.slug === 'heirloom'
									? `${EditionsRoutes.Artwork}/NNG1`
									: heroItem?.inactive_label_button_url}
								size="md"
								dataCy="drop-button-view-series"
								newTab={page.params.slug !== 'heirloom'}
								class="mx-auto mt-[1rem] w-full max-w-[20.3125rem] lg:hidden"
								fullWidth
							>
								{#if page.params.slug === 'heirloom'}
									View Edition
								{:else}
									{heroItem?.inactive_label_button_text}
								{/if}
							</LinkButton>
						{/if}
					</DropCountdown>
				{:else}
					<DropCountdownButtons
						{productId}
						dataCy="drop"
						{showCardCartButton}
						pastDropPage={!!pastDropType}
						item={heroItem}
						contentKey={registerInterestContentKey}
					/>
				{/if}
			</Container>
		{:else if isDropCountdown(item) || isDropCountdownNoButton(item)}
			<Container
				dataCy="drop-countdown"
				class={classNames('hidden max-w-full px-0 sm:px-0 md:px-0 lg:block', {
					visible: !showCardButtons,
				})}
			>
				<DropCountdown
					dataCy="drop"
					pastDropPage={!!pastDropType}
					{item}
					contentKey={registerInterestContentKey}
				/>
			</Container>
			{#if showRegisterCode}
				<Container
					dataCy="drop-register-interest"
					class={classNames('mb-6 max-w-[720px] md:px-0', {
						visible: !showCardButtons || pastDropType,
					})}
				>
					<RegisterInterestButtons
						dataCy="drop"
						code={showRegisterCode}
						contentKey={registerInterestContentKey}
					/>
				</Container>
			{/if}
		{:else if isDropTabBar(item)}
			<div
				id="drop-tab-bar"
				class="no-scrollbar sticky top-[2.9375rem] z-10 mt-16 w-full overflow-x-scroll border-b border-gray-200 bg-white lg:top-[103px]"
			>
				<Container
					dataCy="drop-tab-bar"
					class={classNames('mx-auto max-w-[720px] px-0 sm:mx-auto sm:px-0')}
				>
					<DropTabBar dataCy="drop" {anchors} />
				</Container>
			</div>
		{:else if isDropSpinsStyles(item)}
			<Container dataCy="drop-spin-styles" class="mb-10 max-w-screen-lg">
				<DropSpinsStyles {item} />
			</Container>
		{:else if isDropSpinsFormat(item)}
			<Container
				dataCy="drop-text-section"
				class={classNames('mt-10 mb-6 max-w-[720px] md:mt-16 md:px-0')}
			>
				<DropTextSection
					{showCardCartButton}
					item={{
						__typename: 'Drop_Text_Section',
						id: 'spins-format',
						markdown: `### Format\n\n---\n\n${item?.format_and_medium_custom_component_text}`,
						inactive_markdown: null,
						text_alignment: null,
						// Every custom component here came through the server loads'
						// parse, which gives this one its tokens.
						markdownTokens:
							(item as ParsedDropSpinsStylesType).formatMarkdownTokens ?? [],
						inactiveMarkdownTokens: [],
					}}
				/>
			</Container>
			<Container dataCy="drop-spin-format" class="mb-10 max-w-screen-lg">
				<DropSpinsFormat {item} />
			</Container>
		{:else if isDropSpinsAlgorithmInformation(item)}
			<Container
				dataCy="drop-spin-algo"
				class={classNames('mt-10 mb-10 max-w-[720px] md:mt-16 md:px-0')}
			>
				<DropSpinsAlgorithmInformation {item} />
			</Container>
		{:else if isDropTheCurrencyAntiCounterfeinting(item)}
			<Container
				dataCy="drop-anti-counterfeinting"
				class="mb-10 max-w-screen-lg"
			>
				<DropTheCurrencyAntiCounterfeinting />
			</Container>
		{:else if isDropGfgArtwork(item)}
			<Container
				dataCy="drop-gfg-artworks"
				class={classNames('mb-10 max-w-[720px] md:px-0')}
			>
				<DropGfgArtwork />
			</Container>
		{:else if isDropComponentBackground(item)}
			<DropComponentBackground {item} />
		{:else if isDropVideo(item)}
			<Container
				dataCy="drop-background"
				class={classNames('mb-6 max-w-[720px] md:px-0')}
			>
				<DropVideo {item} />
			</Container>
		{:else if isDropImageCarousel(item)}
			<Container
				dataCy="drop-image-carousel"
				class={classNames('mb-6 max-w-[720px] md:px-0')}
			>
				<DropImageCarousel {item} />
			</Container>
		{:else if isDropImage(item)}
			<Container
				dataCy="drop-image"
				class={classNames(
					'mb-6',
					item?.max_width
						? 'max-w-screen-xl'
						: classNames('max-w-[720px] md:px-0')
				)}
			>
				<!-- TODO check legends -->

				<DropImage {item} showLegend />
			</Container>
		{:else if isDropTimeline(item)}
			<Container
				dataCy="drop-timeline"
				class={classNames('mt-10 max-w-[720px] md:px-0')}
			>
				<DropTimeline {item} />
			</Container>
		{:else if isDropFaqSection(item)}
			<Container
				dataCy="drop-faq-section"
				class={classNames('mt-10 max-w-[720px] md:mt-16 md:px-0')}
			>
				<DropFaqSection
					hasTabs={!!anchors?.length}
					hideIntroText={isDropWhatsappButton(componentItems?.[index + 1])}
					pastDrop={!!pastDropType}
					{item}
				/>
			</Container>
		{:else if isDropProductCardGrid(item)}
			{@const nextComponent = componentItems?.[index + 1]}
			<Container
				dataCy="drop-product-card-grid"
				style={item.top_margin != null
					? `margin-top: ${item.top_margin}px;`
					: undefined}
				class={classNames(
					'md:max-w-screen-lg',
					{
						'md:max-w-screen-xl':
							getDropProductCardGridColumns(
								item.desktop_columns,
								item.is_compact
							) === 4,
					},
					item.show_separator
						? 'mt-6 mb-0 md:mt-8 md:mb-[-1rem]'
						: 'my-10 md:my-16'
				)}
			>
				<DropVariants
					variants={getDropCardVariants(item.variants, PRODUCT_GRID_VARIANTS)}
					sectionId={item.id}
					hasTabs={!!anchors?.length}
				>
					{#snippet children(variant, withAnchor)}
						<DropProductCardGrid
							dataCy="drop-product"
							{dropConfigId}
							hasTabs={!!anchors?.length}
							{showCardCartButton}
							{item}
							{variant}
							{withAnchor}
							nextButtonProps={isDropButton(nextComponent)
								? {
										showDownloadIcon: getDropButtonShowDownloadIcon(
											getDropButtonLink(showCardCartButton, nextComponent)
										),
										label: getDropButtonText(showCardCartButton, nextComponent),
										variant: getDropButtonVariant(nextComponent),
										href: getDropButtonLink(showCardCartButton, nextComponent),
									}
								: null}
						/>
					{/snippet}
				</DropVariants>
			</Container>
		{:else if isDropLogos(item)}
			<Container
				dataCy="drop-logos"
				class={classNames('mb-6 max-w-[720px] md:px-0')}
			>
				<DropLogos {item} />
			</Container>
		{:else if isDropButton(item)}
			{@const prevComponent = componentItems?.[index - 1]}
			{@const shouldHideButton = (button: typeof item) =>
				shouldHideDropButton(button, { showCardButtons, showCardCartButton })}
			{#if isDropButton(componentItems[index + 1]) || (isDropWhatsappButton(componentItems[index + 1]) && !isDropButton(prevComponent)) || isDropProductCardGrid(prevComponent)}
				<span></span>
			{:else if isDropButton(prevComponent)}
				{@const hidePrevButton = shouldHideButton(prevComponent)}
				{@const hideCurrButton = shouldHideButton(item)}
				{#if !hidePrevButton && !hideCurrButton}
					<Container
						dataCy="drop-button"
						class={classNames(
							'flex justify-center md:max-w-screen-lg',
							'my-10'
						)}
					>
						<div
							class="xs:grid xs:grid-flow-col xs:flex-row xs:gap-4 flex auto-cols-fr flex-col gap-2"
						>
							<DropButton
								{showCardButtons}
								{showCardCartButton}
								item={prevComponent}
								class="flex-1 [&>button]:w-full"
							/>
							<DropButton
								{showCardButtons}
								{showCardCartButton}
								{item}
								class="flex-1 [&>button]:w-full"
							/>
						</div>
					</Container>
				{:else if !hidePrevButton}
					<Container
						dataCy="drop-button"
						class="my-10 flex justify-center md:max-w-screen-lg"
					>
						<DropButton
							{showCardButtons}
							{showCardCartButton}
							item={prevComponent}
						/>
					</Container>
				{:else if !hideCurrButton}
					<Container
						dataCy="drop-button"
						class="my-10 flex justify-center md:max-w-screen-lg"
					>
						<DropButton {showCardButtons} {showCardCartButton} {item} />
					</Container>
				{/if}
				{#if !countdownItem && !componentItems
						.slice(0, index - 2 < 0 ? 0 : index - 2)
						.some(isDropButton)}
					{#if showRegisterCode}
						<Container
							dataCy="drop-register-interest"
							class={classNames('mb-10 max-w-[720px] md:px-0', {
								visible: !showCardButtons,
							})}
						>
							<RegisterInterestButtons
								dataCy="drop"
								code={showRegisterCode}
								contentKey={registerInterestContentKey}
							/>
							<hr class="mt-10" />
						</Container>
					{/if}
				{/if}
			{:else if !shouldHideButton(item)}
				<Container
					dataCy="drop-button"
					class="my-10 flex justify-center md:max-w-screen-lg"
				>
					<DropButton {showCardButtons} {showCardCartButton} {item} />
				</Container>
				{#if !countdownItem && !componentItems
						.slice(0, index - 1 < 0 ? 0 : index - 1)
						.some(isDropButton)}
					{#if showRegisterCode}
						<Container
							dataCy="drop-register-interest"
							class={classNames('mb-10 max-w-[720px] md:px-0', {
								visible: !showCardButtons,
							})}
						>
							<RegisterInterestButtons
								dataCy="drop"
								code={showRegisterCode}
								contentKey={registerInterestContentKey}
							/>
						</Container>
					{/if}
				{/if}
			{/if}
		{:else if isDropWhatsappButton(item)}
			{@const prevComponent = componentItems?.[index - 1]}
			{@const pairedButton =
				isDropButton(prevComponent) &&
				!isDropButton(componentItems?.[index - 2])
					? prevComponent
					: null}
			{#if pairedButton}
				{@const hidePairedButton = shouldHideDropButton(pairedButton, {
					showCardButtons,
					showCardCartButton,
				})}
				<Container
					dataCy="drop-whatsapp-button"
					class="my-10 flex justify-center md:max-w-screen-lg"
				>
					{#if hidePairedButton}
						<DropWhatsappButton />
					{:else}
						<div
							class="xs:grid xs:grid-flow-col xs:flex-row xs:gap-4 flex auto-cols-fr flex-col gap-2"
						>
							<DropButton
								{showCardButtons}
								{showCardCartButton}
								item={pairedButton}
								class="flex-1 [&>button]:w-full"
							/>
							<DropWhatsappButton class="flex-1 [&>button]:w-full" />
						</div>
					{/if}
				</Container>
			{:else}
				<Container
					dataCy="drop-whatsapp-button"
					class="my-10 flex justify-center md:max-w-screen-lg"
				>
					<DropWhatsappButton
						introText={isDropFaqSection(prevComponent)
							? prevComponent.introTextTokens
							: null}
					/>
				</Container>
			{/if}
		{:else if isDropGallery(item)}
			<Container dataCy="drop-gallery" class="mb-6 md:max-w-screen-lg">
				<DropGallery {item} />
			</Container>
		{:else if isDropNewsletter(item)}
			<Container
				dataCy="drop-newsletter"
				class={classNames('mb-6 max-w-[720px] md:px-0')}
			>
				<DropNewsletter {item} />
			</Container>
		{:else if isDropPlatform(item)}
			<Container
				dataCy="drop-platform"
				class={classNames('my-6 max-w-[720px] md:px-0')}
			>
				<DropPlatform {item} />
			</Container>
		{:else if isDropQuote(item)}
			<Container
				dataCy="drop-quote"
				class={classNames('mb-6 max-w-[720px] md:px-0')}
			>
				<DropQuote {item} />
			</Container>
		{:else if isDropAnchor(item)}
			{@const anchorIndex = (componentItems || [])
				.slice(0, index - 1)
				.filter((componentItem) => isDropAnchor(componentItem)).length}
			<span bind:this={anchorRefs[anchorIndex]}></span>
			<DropAnchor hasTabs={!!anchors?.length} {item} />
		{:else if isDropTabBarEnd(item)}
			<DropTopBarEnd />
		{:else if isDropProductCards(item)}
			<Container
				dataCy="drop-product-cards"
				class="my-10 md:my-16 md:max-w-screen-lg"
			>
				<DropProductCards
					hasTabs={!!anchors?.length}
					{showCardButtons}
					{showCardCartButton}
					{item}
					{dropConfigId}
				/>
			</Container>
		{:else if isDropSlideshow(item)}
			<Container
				dataCy="drop-slideshow"
				class="my-10 md:my-16 md:max-w-screen-lg"
			>
				<DropSlideshow {item} />
			</Container>
		{:else if isDropCarousel(item)}
			<Container
				dataCy="drop-carousel"
				class="mt-10 md:mt-16 md:max-w-screen-lg"
			>
				<DropCarousel {showCardCartButton} hasTabs={!!anchors?.length} {item} />
			</Container>
		{:else if isDropHybridProductCardGrid(item)}
			<Container
				dataCy="drop-hybrid-product-card-grid"
				class="my-10 md:my-16 md:max-w-screen-xl"
			>
				<DropVariants
					variants={getDropCardVariants(item.variants, HYBRID_GRID_VARIANTS)}
					sectionId={item.id}
					hasTabs={!!anchors?.length}
				>
					{#snippet children(variant, withAnchor)}
						<DropHybridProductCardGrid
							dataCy="drop-hybrid-product"
							{item}
							hasTabs={!!anchors?.length}
							{showCardCartButton}
							{dropConfigId}
							hybridConfig={page.data.hybridConfig}
							loadedPdps={page.data.hybridPdps}
							{variant}
							{withAnchor}
						/>
					{/snippet}
				</DropVariants>
			</Container>
		{:else if isDropHybridFormats(item)}
			<Container
				dataCy="drop-hybrid-formats"
				class="my-10 md:my-16 md:max-w-screen-lg"
			>
				<DropHybridFormats {item} hybridConfig={page.data?.hybridConfig} />
			</Container>
		{:else if isDropGFGPatches(item)}
			<Container
				dataCy="drop-spin-format"
				class={classNames('mb-10 max-w-[720px] md:px-0')}
			>
				<DropGfgPatches />
			</Container>
		{:else if isDropNftVideo(item)}
			<Container
				dataCy="drop-nft-video"
				class={classNames('mb-10 max-w-[720px] md:px-0')}
			>
				<DropNftVideo />
			</Container>
		{:else if isDropMadcCarousel(item)}
			<Container dataCy="drop-madc-carousel" class="mb-10 md:max-w-screen-lg">
				<DropMadcCarousel {item} />
			</Container>
		{/if}
	{/each}
</div>
